import { Task } from '@lit/task';
import { html, nothing, LitElement, css } from 'lit';
import { customElement } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import defer from 'defer-promise';

// Declare global window properties

/**
 * Usage:
 * Control the icon size by supplying relevant font-size class to HOST element
 * <svg-icon
 *     src='icon-close'
 *     width='24px'
 *     viewBox='0 0 24 24'
 *   ></svg-icon>
 */
@customElement('svg-icon')
export default class SvgIcon extends LitElement {
  static loadingCartPromise = null;

  static svgSpriteCache = null;

  static metaPathSel = `meta[data-svg-sprite-path]`;

  static styles = css`
    svg {
      display: block;
    }
  `;

  loadSpriteTask = new Task(this, {
    task: async () => {
      if (SvgIcon.svgSpriteCache) {
        return SvgIcon.svgSpriteCache;
      }

      if (SvgIcon.loadingCartPromise) {
        return SvgIcon.loadingCartPromise.promise;
      }
      SvgIcon.loadingCartPromise = defer();

      try {
        if (window.Shopify?.designMode) {
          // In design mode, fetch and inject the sprite for preview
          const spritePath = this.svgSpritePath;
          if (!spritePath) {
            return nothing;
          }

          const response = await fetch(spritePath);
          if (!response.ok) {
            throw new Error('Network response was not ok');
          }
          const svgText = await response.text();
          // Clean up the fetched SVG text if necessary
          const parser = new DOMParser();
          const doc = parser.parseFromString(svgText, 'image/svg+xml');
          const svg = doc.documentElement;
          if (svg instanceof SVGElement) {
            SvgIcon.svgSpriteCache = this.hideAndGetSvgHtml(svg);

            if (SvgIcon.loadingCartPromise) {
              SvgIcon.loadingCartPromise.resolve();
              SvgIcon.loadingCartPromise = null;
            }

            return SvgIcon.svgSpriteCache;
          }
        }

        // In production mode, don't fetch or inject the sprite
        if (SvgIcon.loadingCartPromise) {
          SvgIcon.loadingCartPromise.resolve();
          SvgIcon.loadingCartPromise = null;
        }

        return nothing;
      } catch (error) {
        if (SvgIcon.loadingCartPromise) {
          SvgIcon.loadingCartPromise.reject();
          SvgIcon.loadingCartPromise = null;
        }
        /* eslint-disable-next-line no-console */
        console.error('Error loading SVG sprite:', error);
        return error;
      }
    },
  });

  hideAndGetSvgHtml(element) {
    // Hide the sprite
    // eslint-disable-next-line no-param-reassign
    element.style.display = 'none';
    return element.outerHTML;
  }

  constructor() {
    super();
    this.metaEl = document.head.querySelector(SvgIcon.metaPathSel);
  }

  connectedCallback() {
    super.connectedCallback();
    if (window.Shopify?.designMode) {
      this.loadSpriteTask.run();
    }
  }

  // PROPERTIES
  static get properties() {
    return {
      src: { type: String },
      ariaLabel: { type: String, attribute: 'aria-label' },
      width: { type: String },
      height: { type: String },
      classes: { type: String },
      customStyles: { type: String, attribute: 'custom-styles' },
    };
  }

  // Holds meta element reference
  metaEl = undefined;

  get svgSpritePath() {
    return this.metaEl?.dataset.svgSpritePath;
  }

  get useHref() {
    if (window.Shopify?.designMode) {
      return `#${this.src}`;
    }
    return `${this.svgSpritePath}#${this.src}`;
  }

  generateSVGMarkup(icon) {
    return html`
      <svg
        aria-label=${this.ariaLabel || nothing}
        aria-hidden=${this.ariaLabel ? nothing : 'true'}
        width=${this.width || '24px'}
        height=${this.height || '24px'}
        part="icon"
        class=${this.classes}
        style=${this.customStyles}
      >
        <use crossorigin="anonymous" href=${icon}></use>
      </svg>
    `;
  }

  render() {
    if (this.src === undefined || this.src === null || this.src === '') {
      return html`${nothing}`;
    }

    if (window.Shopify?.designMode) {
      return html`
        ${this.loadSpriteTask.render({
          complete: () => {
            if (
              !SvgIcon.svgSpriteCache ||
              typeof SvgIcon.svgSpriteCache !== 'string'
            ) {
              return nothing;
            }

            return html`
              ${unsafeHTML(SvgIcon.svgSpriteCache)}
              ${this.src && this.generateSVGMarkup(`#${this.src}`)}
            `;
          },
        })}
      `;
    }

    return this.generateSVGMarkup(this.useHref);
  }
}
